import { useState, useEffect, useRef } from 'react'
import { View, Text, TouchableOpacity, StyleSheet, Share } from 'react-native'
import ViewShot from 'react-native-view-shot'
import { useTranslation } from 'react-i18next'
import { supabase } from '../../lib/supabase'
import { colors, fonts, radius } from '../../lib/theme'
import { useThemeColors } from '../../lib/useThemeColors'
import { cacheGet, cacheSet, CACHE_KEY } from './cache'
import { EmptyState, SectionLabel } from './ui'
import { Podio, ListaClassifica, MyRank, tiedPositions, posOf } from './Classifica'
import { ListSkeleton, ErrorState, StaleBanner } from '../StateViews'
import ClassificaShareCard, { SHARE_W } from './ClassificaShareCard'
import { shareViewAsImage } from '../../lib/shareImage'
import { useAuth } from '../../lib/AuthContext'
import LeaderHero from './LeaderHero'
import GradientButton from '../GradientButton'
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons'
export default function ClassificaSingola({ idStage, leagueId, currentUserId, onPlayerClick, raceName }) {
  const { t } = useTranslation()
  const tc = useThemeColors()
  const styles = makeStyles(tc)
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)
  const [sharing, setSharing] = useState(false)
  const { leagueName } = useAuth()
  const viewShotRef = useRef(null)
  const [stats, setStats] = useState({})   // id_user -> { top10, hasWinner } (solo gara conclusa)

  useEffect(() => {
    if (!idStage || !leagueId) return
    let cancelled = false
    const load = async () => {
      const cacheKey = CACHE_KEY(leagueId, idStage, 'singola')
      const cached = await cacheGet(cacheKey)
      if (cached) { setData(cached); setLoading(false) }
      else setLoading(true)

      setError(false)
      const { data: rows, error: rpcError } = await supabase
        .rpc('fn_simulazione_punti', { p_id_league: leagueId })
        .select('posizione_classifica, id_user, display_name, punti_totali')
        .eq('id_stage', idStage)
        .order('posizione_classifica')

      if (cancelled) return
      if (rpcError) { setError(true); setLoading(false); return }       // resta quello in cache, se c'e'
      const result = rows ?? []
      setData(result)
      setLoading(false)
      cacheSet(cacheKey, result)

      // Per ogni giocatore: quanti dei suoi corridori sono arrivati nei primi 10 e se ha il vincitore
      const { data: picks } = await supabase.from('fact_user_pick').select('id_user, id_rider').eq('id_stage', idStage).eq('id_league', leagueId)
      const riderIds = [...new Set((picks ?? []).map(p => p.id_rider))]
      if (!riderIds.length || cancelled) return
      const { data: res } = await supabase.from('fact_results').select('id_rider, rank_stage').eq('id_stage', idStage).eq('is_published', true).in('id_rider', riderIds)
      if (cancelled) return
      const rankOf = Object.fromEntries((res ?? []).map(r => [r.id_rider, r.rank_stage]))
      const next = {}
      ;(picks ?? []).forEach(p => {
        const rk = rankOf[p.id_rider]
        const st = (next[p.id_user] ??= { top10: 0, hasWinner: false })
        if (rk != null && rk <= 10) st.top10 += 1
        if (rk === 1) st.hasWinner = true
      })
      setStats(next)
    }
    load()
    return () => { cancelled = true }
  }, [idStage, leagueId, reloadKey])

  // Immagine della classifica (disegno fisso); se la cattura non riesce, ripiego sul testo semplice
  const handleShare = async () => {
    setSharing(true)
    try {
      await shareViewAsImage(viewShotRef, {
        fileName: `virtua-classifica-${(raceName ?? '').replace(/\s+/g, '-')}`,
        dialogTitle: raceName ?? t('leaderboard.title'),
      })
    } catch {
      try {
        const text = data.slice(0, 10).map(r => `${r.posizione_classifica}. ${r.display_name} — ${r.punti_totali} pt`).join('\n')
        await Share.share({ message: t('leaderboard.share_text', { name: raceName ?? t('leaderboard.title'), text, site: 'virtuacycling.com' }) })
      } catch {/* ignore */ }
    } finally { setSharing(false) }
  }

  const retry = () => { setLoading(true); setReloadKey(k => k + 1) }
  if (loading) return <ListSkeleton />
  if (error && data.length === 0) return <ErrorState onRetry={retry} />
  if (data.length === 0) return (
    <EmptyState icon="🏁" title={t('leaderboard.no_result_title')} subtitle={t('leaderboard.no_result_sub')} />
  )

  const rest = data.slice(3)
  const tied = tiedPositions(data)
  const shareRows = data.map((r, i) => ({ id_user: r.id_user, pos: posOf(r, i), name: r.display_name, value: `${r.punti_totali} pt`, tie: tied.has(posOf(r, i)) }))
  // sotto il nome: i corridori nei primi 10 e il vincitore, se ce l'ha
  const renderSub = (row) => {
    const st = stats[row.id_user]
    if (!st) return null
    return (
      <View style={styles.subRow}>
        <Text style={[styles.subText, { color: tc.textMuted }]} numberOfLines={1}>{t('leaderboard.top10_count', { count: st.top10 })}</Text>
        {st.hasWinner && <MaterialCommunityIcons name="trophy-outline" size={12} color={tc.rosaText} />}
      </View>
    )
  }
  return (
    <>
    {error && <StaleBanner onRetry={retry} />}
    <View style={[styles.card, { backgroundColor: tc.bgCard, borderColor: tc.border }]}>
      {/* Header con SectionLabel + Condividi */}
      <View style={styles.cardHeader}>
        <SectionLabel>{t('leaderboard.gc_rank')}</SectionLabel>
        <GradientButton
          onPress={handleShare}
          loading={sharing}
          size="sm"
          icon={<MaterialCommunityIcons name="share-variant-outline" size={13} color={tc.brandText} />}
        >
          {sharing ? t('leaderboard.sharing') : t('leaderboard.share')}
        </GradientButton>
      </View>

      <MyRank data={data} currentUserId={currentUserId} valueKey="punti_totali" valueLabel="pt" tied={tied} onPlayerClick={onPlayerClick} numOf={(r) => Number(r.punti_totali ?? 0)} />

      {/* Gara di un giorno: il vincitore con la medaglia d'oro al collo (non c'e' maglia di classifica) */}
      <LeaderHero
        userId={data[0].id_user}
        name={data[0].display_name}
        color={tc.rosaText}
        label={t('leaderboard.winner')}
        value={`${data[0].punti_totali} pt`}
        sub={data[1] ? t('leaderboard.lead_over_second', { gap: `+${(data[0].punti_totali ?? 0) - (data[1].punti_totali ?? 0)} pt` }) : ''}
        medal="gold"
        chipColor="#FACC15"
        keepJersey
        onPress={() => onPlayerClick?.({ id: data[0].id_user, name: data[0].display_name })}
      />
      <Podio
        data={data}
        nameKey="display_name"
        valueKey="punti_totali"
        valueLabel="pt"
        currentUserId={currentUserId}
        userIdKey="id_user"
        onPlayerClick={onPlayerClick}
        tied={tied}
        renderSub={renderSub}
      />
      {rest.length > 0 && (
        <ListaClassifica
          data={rest}
          nameKey="display_name"
          valueKey="punti_totali"
          valueLabel="pt"
          currentUserId={currentUserId}
          userIdKey="id_user"
          onPlayerClick={onPlayerClick}
          tied={tied}
          renderSub={renderSub}
          offset={3}
        />
      )}

      {/* ViewShot nascosto per share */}
      <ViewShot
        ref={viewShotRef}
        options={{ format: 'png', quality: 1, pixelRatio: 3 }}
        style={{ position: 'absolute', top: -9999, left: 0, width: SHARE_W }}
      >
        <ClassificaShareCard raceName={raceName} rows={shareRows} currentUserId={currentUserId} leagueName={leagueName} />
      </ViewShot>
    </View>
    </>
  )
}

const makeStyles = (tc) => StyleSheet.create({
  loadingWrap: { padding: 20 },
  card: { borderRadius: 14, borderWidth: 1, padding: 18, marginHorizontal: 16, marginBottom: 16 },
  cardHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 },
  subRow: { flexDirection: 'row', alignItems: 'center', gap: 4, marginTop: 1 },
  subText: { fontSize: 11 },
})
